import type {
  AutomationSafetyDiagnosticPage,
  LoopPathSegment,
  ReadinessIssueCode,
} from '@streamlain/automation-safety-contract';

export {
  type AutomationSafetyPresentation,
  presentAutomationSafety,
} from '@streamlain/automation-safety-contract';

export function isCurrentAutomationSafety(
  page: AutomationSafetyDiagnosticPage,
  workflowId: string,
  configId: string
): boolean {
  return (
    page.subject.workflowId === workflowId && page.subject.configId === configId
  );
}

type AutomationSafetyWaitOptions = {
  timeoutMs?: number;
  intervalMs?: number;
  now?: () => number;
  delay?: (milliseconds: number) => Promise<void>;
};

export async function waitForCurrentAutomationSafety(
  load: () => Promise<AutomationSafetyDiagnosticPage | null>,
  workflowId: string,
  configId: string,
  options: AutomationSafetyWaitOptions = {}
): Promise<AutomationSafetyDiagnosticPage | null> {
  const timeoutMs = options.timeoutMs ?? 15_000;
  const intervalMs = options.intervalMs ?? 500;
  const now = options.now ?? Date.now;
  const delay =
    options.delay ??
    ((milliseconds: number) =>
      new Promise(resolve => globalThis.setTimeout(resolve, milliseconds)));
  const deadline = now() + timeoutMs;
  let latest: AutomationSafetyDiagnosticPage | null = null;

  while (true) {
    const result = await load();
    if (result && isCurrentAutomationSafety(result, workflowId, configId)) {
      latest = result;
      if (result.topology.state !== 'CANDIDATE_PENDING') {
        return result;
      }
    }

    const remaining = deadline - now();
    if (remaining <= 0) return latest;
    await delay(Math.min(intervalMs, remaining));
  }
}

/** Readiness gap the server names when it refuses a mail folder as source. */
const ROWLESS_SOURCE_CODE: ReadinessIssueCode = 'input.source-rowless';
/** Readiness gap the server names when an output target's table was deleted. */
const TARGET_MISSING_CODE: ReadinessIssueCode = 'output.target-missing';
/** The server refuses while another version of the same App automation runs. */
const OTHER_APP_VERSION_ON_CODE = 'AUTOMATION_OTHER_APP_VERSION_ON';
/** The server refuses a version of an App the published App Container does not show. */
const APP_VERSION_NOT_PUBLISHED_CODE = 'AUTOMATION_APP_VERSION_NOT_PUBLISHED';

/**
 * Stable code of a refused request. The workspace API client hands the
 * server's code over as `code` of its error; the error's message is the
 * server's sentence without the code.
 */
function refusalCode(error: unknown): string | null {
  const code = (error as { code?: unknown } | null)?.code;
  return typeof code === 'string' ? code : null;
}

/**
 * Message for a refused activation. The server's answer carries a stable
 * code; a gap the sidebar also knows gets the label the sidebar shows for it.
 */
export function automationSafetyEnableFailureKey(
  error: unknown
):
  | 'com.streamlain.automation-safety.enable-failed'
  | 'com.streamlain.automation-safety.reconfigure'
  | 'com.streamlain.automation-safety.other-app-version-on'
  | 'com.streamlain.automation-safety.app-version-not-published'
  | 'com.streamlain.automation.sidebar.source-mail-folder'
  | 'com.streamlain.automation.sidebar.empty-target.message' {
  const code = refusalCode(error);
  if (code === ROWLESS_SOURCE_CODE) {
    return 'com.streamlain.automation.sidebar.source-mail-folder';
  }
  if (code === TARGET_MISSING_CODE) {
    return 'com.streamlain.automation.sidebar.empty-target.message';
  }
  if (code === OTHER_APP_VERSION_ON_CODE) {
    return 'com.streamlain.automation-safety.other-app-version-on';
  }
  if (code === APP_VERSION_NOT_PUBLISHED_CODE) {
    return 'com.streamlain.automation-safety.app-version-not-published';
  }
  const message = error instanceof Error ? error.message : String(error);
  return message.includes('AUTOMATION_HTTP_TARGET_RECONFIGURE')
    ? 'com.streamlain.automation-safety.reconfigure'
    : 'com.streamlain.automation-safety.enable-failed';
}

/** One step of a loop path as the sidebar shows it. */
export type LoopPathStep =
  | { kind: 'hidden' }
  | {
      kind: 'step';
      resourceType: LoopPathSegment['resourceType'];
      /** The stored name; null for a table without name. */
      label: string | null;
      /** The doc and block the step opens; null without stored link. */
      target: { docId: string; blockId: string } | null;
    };

/**
 * A step of another workspace stays hidden; a visible step carries its name
 * and the doc and block it opens (the automation block or the table).
 */
export function presentLoopPathSegment(segment: LoopPathSegment): LoopPathStep {
  if (segment.opaque) return { kind: 'hidden' };
  return {
    kind: 'step',
    resourceType: segment.resourceType,
    label: segment.displayName ?? segment.stepId ?? segment.configId ?? null,
    target:
      segment.docId && segment.blockId
        ? { docId: segment.docId, blockId: segment.blockId }
        : null,
  };
}
